import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import LandingPage from './LandingPage';
import App from './App';
import Login from './Login';
import ForgotPassword from './ForgotPassword';
import ResetPassword from './ResetPassword';
import VerifyEmail from './VerifyEmail';
import ManageUsersPage from './ManageUsersPage';
import ClientDetailPage from './ClientDetailPage';
import MyTasksPage from './MyTasksPage';
import ActivityLogPage from './ActivityLogPage';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { clearSession, getToken, validateSession } from './auth';
import axios from 'axios';

const API_ROOT = process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_API_URL || '')
  : (process.env.REACT_APP_API_URL || 'http://localhost:5001');
axios.defaults.baseURL = API_ROOT;

// Register service worker for PWA functionality
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        console.log('SW registered: ', registration);
      })
      .catch((registrationError) => {
        console.log('SW registration failed: ', registrationError);
      });
  });
}

const RequireAuth = ({ children }) => {
  const location = useLocation();
  const [status, setStatus] = useState('checking'); // checking | ok | fail

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      const token = getToken();
      if (!token) {
        if (!cancelled) setStatus('fail');
        return;
      }
      const user = await validateSession();
      if (cancelled) return;
      if (!user) {
        setStatus('fail');
        return;
      }
      setStatus('ok');
    };

    check();

    // If user presses Back and page is restored from bfcache without a token, force login
    const onPageShow = (event) => {
      if (event.persisted && !getToken()) {
        clearSession();
        window.location.replace('/login');
      } else if (event.persisted) {
        check();
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => {
      cancelled = true;
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [location.pathname]);

  if (status === 'checking') {
    return (
      <div style={{ minHeight: '40vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        Checking session…
      </div>
    );
  }

  if (status === 'fail') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return children;
};

const RequireAdmin = ({ children }) => {
  const role = localStorage.getItem('role');
  if (role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/dashboard" element={<RequireAuth><App /></RequireAuth>} />
        <Route path="/clients/:id" element={<RequireAuth><ClientDetailPage /></RequireAuth>} />
        <Route path="/tasks" element={<RequireAuth><MyTasksPage /></RequireAuth>} />
        <Route path="/admin/users" element={<RequireAuth><RequireAdmin><ManageUsersPage /></RequireAdmin></RequireAuth>} />
        <Route path="/admin/activity" element={<RequireAuth><RequireAdmin><ActivityLogPage /></RequireAdmin></RequireAuth>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  </React.StrictMode>
);
